<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  ArrowLeft,
  Baby,
  CalendarDays,
  Check,
  CircleHelp,
  FerrisWheel,
  Heart,
  Home,
  Moon,
  PartyPopper,
  Sun,
  Trees,
  X
} from 'lucide-vue-next';
import { useFamily, type PlayRequestOption } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';

const placeOptions: PlayRequestOption[] = [
  { type: 'my_home', label: 'Bei mir', icon: 'my-home' },
  { type: 'your_home', label: 'Bei dir', icon: 'friend-home' },
  { type: 'playground', label: 'Spielplatz', icon: 'playground' }
];

const timeOptions: PlayRequestOption[] = [
  { type: 'today', label: 'Heute', icon: 'sun' },
  { type: 'tomorrow', label: 'Morgen', icon: 'calendar' },
  { type: 'weekend', label: 'Am Wochenende', icon: 'party' },
  { type: 'parents_choose', label: 'Mama/Papa entscheiden', icon: 'question' }
];

const { createPlayRequest, selectedChild, selectedFriend } = useFamily();
const { navigate } = useNavigation();

const step = ref<1 | 2 | 3 | 4>(1);
const selectedPlace = ref<PlayRequestOption | null>(null);
const selectedTime = ref<PlayRequestOption | null>(null);
const spokenPlaceType = ref('');
const spokenTimeType = ref('');
const errorText = ref('');
const isSpeaking = ref(false);
const isSubmitting = ref(false);

const friendName = computed(() => selectedFriend.value?.displayName ?? 'Freund');

function choosePlace(option: PlayRequestOption) {
  if (spokenPlaceType.value === option.type && !isSpeaking.value) {
    selectedPlace.value = option;
    errorText.value = '';
    step.value = 2;
    return;
  }

  speakOption(spokenPlaceTextFor(option), () => {
    spokenPlaceType.value = option.type;
  });
}

function chooseTime(option: PlayRequestOption) {
  if (spokenTimeType.value === option.type && !isSpeaking.value) {
    selectedTime.value = option;
    errorText.value = '';
    step.value = 3;
    return;
  }

  speakOption(spokenTimeTextFor(option), () => {
    spokenTimeType.value = option.type;
  });
}

async function submitPlayRequest() {
  if (isSubmitting.value) return;
  if (!selectedPlace.value || !selectedTime.value || !selectedChild.value || !selectedFriend.value) {
    errorText.value = 'Oh, da fehlt noch etwas';
    return;
  }

  isSubmitting.value = true;
  const request = await createPlayRequest({
    place: selectedPlace.value,
    time: selectedTime.value
  }).catch(() => null);
  isSubmitting.value = false;

  if (!request) {
    errorText.value = 'Oh, da fehlt noch etwas';
    return;
  }

  step.value = 4;
}

function goBack() {
  if (step.value === 1) {
    navigate('friend');
    return;
  }
  if (step.value === 2) {
    step.value = 1;
    spokenTimeType.value = '';
  }
  if (step.value === 3) {
    step.value = 2;
    spokenTimeType.value = selectedTime.value?.type ?? '';
  }
  if (step.value === 4) navigate('friend');
}

function iconFor(option: PlayRequestOption) {
  if (option.icon === 'my-home' || option.icon === 'friend-home') return Home;
  if (option.icon === 'trees') return Trees;
  if (option.icon === 'moon') return Moon;
  if (option.icon === 'heart') return Heart;
  if (option.icon === 'calendar') return CalendarDays;
  if (option.icon === 'party') return PartyPopper;
  if (option.icon === 'question') return CircleHelp;
  if (option.icon === 'playground') return FerrisWheel;
  return Sun;
}

function speakOption(text: string, afterSpeech: () => void) {
  if (isSpeaking.value) return;
  isSpeaking.value = true;

  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    isSpeaking.value = false;
    afterSpeech();
    return;
  }

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'de-DE';
  utterance.rate = 0.86;
  utterance.pitch = 1.05;
  utterance.volume = 1;
  utterance.voice = naturalGermanVoice();

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    isSpeaking.value = false;
    afterSpeech();
  };

  utterance.addEventListener('end', finish);
  utterance.addEventListener('error', finish);
  window.setTimeout(finish, 1800);
  window.speechSynthesis.speak(utterance);
}

function naturalGermanVoice() {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null;

  const voices = window.speechSynthesis.getVoices();
  const germanVoices = voices.filter((voice) => voice.lang.toLowerCase().startsWith('de'));
  return (
    germanVoices.find((voice) => /natural|online|neural/i.test(voice.name)) ??
    germanVoices.find((voice) => /katja|hedda|anna|markus/i.test(voice.name)) ??
    germanVoices[0] ??
    null
  );
}

function spokenPlaceTextFor(option: PlayRequestOption) {
  if (option.type === 'my_home') return 'Bei mir zuhause';
  if (option.type === 'your_home') return `Bei ${friendName.value} zuhause`;
  if (option.type === 'playground') return 'Auf dem Spielplatz';
  return option.label;
}

function spokenTimeTextFor(option: PlayRequestOption) {
  if (option.type === 'today') return 'Heute';
  if (option.type === 'tomorrow') return 'Morgen';
  if (option.type === 'weekend') return 'Am Wochenende';
  if (option.type === 'parents_choose') return 'Mama oder Papa entscheiden';
  return option.label;
}
</script>

<template>
  <section class="mx-auto max-w-6xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-full bg-white px-5 font-black text-heart-ink shadow-sm" type="button" @click="goBack">
        <ArrowLeft class="h-5 w-5" aria-hidden="true" />
        Zurueck
      </button>
      <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-full bg-white px-5 font-black text-heart-ink shadow-sm" type="button" @click="navigate('friend')">
        <X class="h-5 w-5" aria-hidden="true" />
        Abbrechen
      </button>
    </div>

    <div v-if="step === 1" class="mt-8">
      <h1 class="text-4xl font-black text-heart-berry md:text-6xl">Wo moechtest du spielen?</h1>
      <div class="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <button
          v-for="option in placeOptions"
          :key="option.type"
          class="focus-ring flex min-h-40 flex-col items-center justify-center gap-4 rounded-[30px] p-5 text-2xl font-black shadow-soft transition hover:-translate-y-1 active:scale-[0.98]"
          :class="spokenPlaceType === option.type ? 'bg-amber-100 text-heart-berry ring-4 ring-heart-coral' : 'bg-white text-heart-ink'"
          type="button"
          :disabled="isSpeaking"
          @click="choosePlace(option)"
        >
          <span v-if="option.icon === 'my-home' || option.icon === 'friend-home'" class="relative grid h-24 w-28 place-items-center">
            <Home
              class="h-24 w-24"
              :class="option.icon === 'my-home' ? 'text-heart-coral' : 'text-sky-700'"
              aria-hidden="true"
            />
            <span
              class="absolute bottom-1 right-1 grid h-14 w-14 place-items-center overflow-hidden rounded-full border-4 border-white text-white shadow-sm"
              :style="{ backgroundColor: option.icon === 'my-home' ? selectedChild.favoriteColor : '#0ea5e9' }"
              aria-hidden="true"
            >
              <img
                v-if="option.icon === 'my-home' && selectedChild.profileImageUrl"
                class="h-full w-full object-cover"
                :src="selectedChild.profileImageUrl"
                alt=""
              />
              <Baby v-else-if="option.icon === 'my-home'" class="h-7 w-7" />
              <span v-else class="text-2xl font-black">{{ friendName.slice(0, 1) }}</span>
            </span>
          </span>
          <span v-else-if="option.icon === 'playground'" class="relative h-20 w-28" aria-hidden="true">
            <span class="absolute left-1 top-2 h-16 w-16 border-l-4 border-r-4 border-t-4 border-heart-coral" />
            <span class="absolute left-7 top-8 h-7 w-5 border-l-2 border-r-2 border-slate-500" />
            <span class="absolute left-5 top-14 h-3 w-9 rounded-full bg-heart-coral" />
            <span class="absolute bottom-2 right-1 h-4 w-14 -rotate-12 rounded-full bg-sky-400" />
            <span class="absolute bottom-2 right-9 h-12 w-4 rounded-t-full bg-heart-leaf" />
          </span>
          <component v-else :is="iconFor(option)" class="h-16 w-16 text-heart-coral" aria-hidden="true" />
          {{ option.label }}
          <span v-if="spokenPlaceType === option.type" class="rounded-full bg-white px-4 py-2 text-sm font-black text-heart-leaf">
            Nochmal tippen
          </span>
        </button>
      </div>
    </div>

    <div v-else-if="step === 2" class="mt-8">
      <h1 class="text-4xl font-black text-heart-berry md:text-6xl">Wann moechtest du spielen?</h1>
      <div class="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <button
          v-for="option in timeOptions"
          :key="option.type"
          class="focus-ring flex min-h-40 flex-col items-center justify-center gap-4 rounded-[30px] p-5 text-2xl font-black shadow-soft transition hover:-translate-y-1 active:scale-[0.98]"
          :class="spokenTimeType === option.type ? 'bg-amber-100 text-heart-berry ring-4 ring-heart-coral' : 'bg-white text-heart-ink'"
          type="button"
          :disabled="isSpeaking"
          @click="chooseTime(option)"
        >
          <component :is="iconFor(option)" class="h-16 w-16 text-heart-leaf" aria-hidden="true" />
          {{ option.label }}
          <span v-if="spokenTimeType === option.type" class="rounded-full bg-white px-4 py-2 text-sm font-black text-heart-leaf">
            Nochmal tippen
          </span>
        </button>
      </div>
    </div>

    <div v-else-if="step === 3" class="mt-8">
      <h1 class="text-4xl font-black text-heart-berry md:text-6xl">Mama oder Papa fragen?</h1>
      <div class="mt-8 rounded-[34px] bg-white p-6 shadow-soft md:p-8">
        <div class="grid gap-4 text-3xl font-black text-heart-ink">
          <p class="inline-flex items-center gap-4"><Trees class="h-12 w-12 text-heart-coral" aria-hidden="true" /> {{ selectedPlace?.label }}</p>
          <p class="inline-flex items-center gap-4"><Moon class="h-12 w-12 text-heart-leaf" aria-hidden="true" /> {{ selectedTime?.label }}</p>
          <p class="inline-flex items-center gap-4"><Heart class="h-12 w-12 fill-heart-coral text-heart-coral" aria-hidden="true" /> Mit {{ friendName }}</p>
        </div>
        <button class="focus-ring mt-8 inline-flex min-h-20 w-full items-center justify-center gap-3 rounded-[26px] bg-heart-coral px-6 text-2xl font-black text-white shadow-soft disabled:opacity-70" type="button" :disabled="isSubmitting" @click="submitPlayRequest">
          <Check class="h-9 w-9" aria-hidden="true" />
          Mama/Papa fragen
        </button>
      </div>
    </div>

    <div v-else class="mt-8 rounded-[34px] bg-white p-8 text-center shadow-soft">
      <Heart class="mx-auto h-20 w-20 fill-heart-coral text-heart-coral" aria-hidden="true" />
      <h1 class="mt-5 text-4xl font-black text-heart-berry md:text-6xl">Deine Spielidee wartet jetzt auf Mama oder Papa</h1>
      <button class="focus-ring mt-8 min-h-14 rounded-full bg-heart-leaf px-6 font-black text-white" type="button" @click="navigate('friend')">
        Fertig
      </button>
    </div>

    <p v-if="errorText" class="mt-6 rounded-2xl bg-white px-4 py-3 text-center text-xl font-black text-heart-berry">
      {{ errorText }}
    </p>
  </section>
</template>
